import { useTranslation } from 'react-i18next';
import { useDocumentParams } from 'app/router/params';
import { useAccessLogs, useAccessLogsFilters } from 'entities/access-log';
import { useState } from 'react';
import { DateTimePicker as RWDateTimePicker, Localization } from 'react-widgets';
import MomentLocalizer from 'react-widgets-moment';

import moment from 'moment';

moment.locale('sv');

interface DateTimePickerProps {
  includeTime?: boolean;
  selectIcon?: React.ReactNode;
  onChange?: (e: Date | null) => void;
  format?: string;
  value?: Date | null;
  defaultValue?: Date | null;
}

const DateTimePicker = RWDateTimePicker as React.ComponentType<DateTimePickerProps>;

interface AccessLogEntry {
  sectionName: string;
  userName: string;
  accessedAt?: string;
  [key: string]: unknown;
}

interface FilterOption {
  id: string | number;
  name: string;
}

interface AccessLogFilters {
  sections?: FilterOption[];
  users?: FilterOption[];
  [key: string]: unknown;
}

interface AccessLogsFilterState {
  document: number[];
  section: string[];
  user: number[];
  startDate: string | null;
  endDate: string | null;
}

interface CheckedEvent {
  key: keyof AccessLogsFilterState;
  value: string | number;
}

export const AccessLogs = () => {
  const { t } = useTranslation('access-logs');

  const [filter, setFilterValues] = useState<AccessLogsFilterState>({
    document: [],
    section: [],
    user: [],
    startDate: null,
    endDate: null,
  });
  const { documentId } = useDocumentParams();

  const { data: accessLogs } = useAccessLogs(filter, documentId) as { data: AccessLogEntry[] | undefined };
  const { data: accessLogsFilters, isLoading: isAccessLogsFiltersLoading } = useAccessLogsFilters(documentId) as {
    data: AccessLogFilters | undefined;
    isLoading: boolean;
  };

  const sectionNames = Array.from(new Set(accessLogs?.map((item) => item.sectionName)));
  const userNames = Array.from(new Set(accessLogs?.map((log) => log.userName)));
  const localizer = new MomentLocalizer(moment);

  const handleOnChecked = (e: CheckedEvent) => {
    const { key, value } = e;
    setFilterValues((prev) => {
      const previousScopeValues = (prev[key] as (string | number)[]) || [];
      const isChecked = previousScopeValues.includes(value);
      return {
        ...prev,
        [key]: isChecked ? previousScopeValues.filter((v) => v !== value) : [...previousScopeValues, value],
      } as AccessLogsFilterState;
    });
  };
  return (
    <>
      <div className="filter-wrapper">
        {!isAccessLogsFiltersLoading && (
          <>
            <CheckBoxFilter values={accessLogsFilters?.sections} onCheckedCallBack={handleOnChecked} filterKey="section" label={t('filter.section')} />
            <CheckBoxFilter values={accessLogsFilters?.users} onCheckedCallBack={handleOnChecked} filterKey="user" label={t('filter.user')} />
          </>
        )}
        <div>
          <h2>{t('filter.start-date')}</h2>
          <Localization date={localizer}>
            <DateTimePicker
              includeTime={true}
              selectIcon={<i className="fa-regular fa-calendar"></i>}
              onChange={(e: Date | null) => {
                if (filter.startDate) {
                  setFilterValues((prev) => ({
                    ...prev,
                    startDate: e ? moment(e, 'YYYY-MM-DD HH:mm').toISOString() : null,
                  }));
                } else {
                  setFilterValues((prev) => ({
                    ...prev,
                    startDate: e ? moment(e, 'YYYY-MM-DD').startOf('day').toISOString() : null,
                  }));
                }
              }}
              format="mmm YYY"
              value={filter.startDate ? new Date(filter.startDate) : null}
            />
          </Localization>
        </div>
        <div>
          <h2>{t('filter.end-date')}</h2>
          <Localization date={localizer}>
            <DateTimePicker
              defaultValue={null}
              includeTime
              selectIcon={<i className="fa-regular fa-calendar"></i>}
              onChange={(e: Date | null) => {
                if (filter.startDate) {
                  setFilterValues((prev) => ({
                    ...prev,
                    endDate: e ? moment(e, 'YYYY-MM-DD HH:mm').toISOString() : null,
                  }));
                } else {
                  setFilterValues((prev) => ({
                    ...prev,
                    endDate: e ? moment(e, 'YYYY-MM-DD').startOf('day').toISOString() : null,
                  }));
                }
              }}
              format="mmm YYY"
            />
          </Localization>
        </div>
      </div>
      <div className="accesslog-table-wrapper">
        <table className="accesslog-table">
          <thead>
            <tr>
              <th key={'empty'}></th>
              {sectionNames?.map((item) => {
                return (
                  <th className="accesslog-table-header" key={item}>
                    {item}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {userNames.map((user) => (
              <tr key={user}>
                <td className="accesslog-table-header name-row">
                  <span>{user}</span>
                </td>
                {sectionNames.map((section) => {
                  const record = accessLogs?.find((r) => r.sectionName === section && r.userName === user);
                  return (
                    <td className="accesslog-table-cell" key={section}>
                      <span>{record ? moment(record.accessedAt).format('YYYY-MM-DD HH:mm') : ''}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

interface CheckBoxFilterProps {
  values: FilterOption[] | undefined;
  onCheckedCallBack: (e: CheckedEvent) => void;
  filterKey: keyof AccessLogsFilterState;
  label: string;
}

const CheckBoxFilter = ({ values, onCheckedCallBack, filterKey, label }: CheckBoxFilterProps) => {
  return (
    <div>
      <h2>{label}</h2>

      <div className="checkbox-filter-wrapper">
        {values?.map((value) => (
          <div key={value.id} className="checkbox-filter-item">
            <input type="checkbox" id={String(value.id)} name={value.name} onChange={() => onCheckedCallBack({ key: filterKey, value: value.id })} />
            <label htmlFor={String(value.id)}>{value.name}</label>
          </div>
        ))}
      </div>
    </div>
  );
};
